import { useState, useCallback } from 'react';
import { Droplets, Check, Info } from 'lucide-react';
import LabBench from '../../LabBench';
import { Dropper } from '@/components/lab';
import InfoBox from '../../InfoBox';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Preparing Halide Solutions',
    content: `Halides are anions of Group 17 elements (F-, Cl-, Br-, I-). In this experiment, we focus on chloride (Cl-) and iodide (I-). Halide ions are formed when halogens gain one electron to achieve a stable octet. These ions are colorless in solution but form distinctively colored precipitates with silver ions, making them easy to identify qualitatively.`
  },
  2: {
    title: 'Silver Nitrate Precipitation Test',
    content: `AgNO₃ is the classic reagent for halide detection. Silver ions (Ag+) form insoluble precipitates with halide ions: Ag+ + X- -> AgX(s). The colors are diagnostic: AgCl is white (Ksp = 1.8x10^-10), AgBr is cream/pale yellow, and AgI is yellow (Ksp = 8.5x10^-17). The varying Ksp values reflect different lattice energies and become important in the ammonia dissolution test.`
  },
  3: {
    title: 'Comparing Precipitate Colors',
    content: `Color comparison is key to halide identification. AgCl appears white or curdy, while AgI is distinctly yellow. The color difference arises from the different electronic structures and polarizabilities of the halide ions. Iodide is larger and more polarizable, leading to greater covalent character in AgI and absorption of higher-energy (blue) light, making it appear yellow.`
  },
  4: {
    title: 'Ammonia Dissolution Test',
    content: `Adding NH₄OH (aqueous ammonia) distinguishes chloride from iodide. AgCl dissolves in ammonia by forming the diamminesilver(I) complex: AgCl + 2NH3 -> [Ag(NH3)2]+ + Cl-. However, AgI does NOT dissolve because its Ksp is much lower - the complex formation constant isn't large enough to overcome AgI's insolubility. This differential solubility is diagnostic.`
  },
  5: {
    title: 'Decanting the Solution',
    content: `Decanting separates the clear supernatant (containing [Ag(NH3)2]+ and Cl-) from any undissolved precipitate. This step isolates the silver-ammonia complex for the next test. Careful decanting avoids transferring precipitate particles. The clear solution contains "hidden" silver that will be revealed when we destroy the ammonia complex.`
  },
  6: {
    title: 'Acid Reprecipitation Test',
    content: `Adding HNO₃ to the decanted solution destroys the ammonia complex and reprecipitates AgCl: [Ag(NH3)2]+ + Cl- + 2H+ -> AgCl(s) + 2NH4+. The acid protonates ammonia, releasing Ag+ which immediately combines with Cl- to reform the white precipitate. This confirms that chloride was present and distinguishes it from iodide (which never dissolved).`
  },
  7: {
    title: 'Confirming Chloride Presence',
    content: `A positive chloride test shows: (1) white precipitate with AgNO₃, (2) dissolution in NH3, and (3) reprecipitation with acid. The full sequence eliminates false positives. Record "Cl- confirmed" when all three steps succeed. The iodide tube should show yellow AgI precipitate that remains undissolved throughout - this differential behavior confirms both halides.`
  }
};

interface Part3HalideProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

export default function Part3Halide({ onStepComplete, currentStep = 1 }: Part3HalideProps) {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [tubesPrepared, setTubesPrepared] = useState(false);
  const [agno3Added, setAgno3Added] = useState(false);
  const [isDropping, setIsDropping] = useState(false);
  const [precipitatesObserved, setPrecipitatesObserved] = useState(false);
  const [nh4ohAdded, setNh4ohAdded] = useState(false);
  const [agclDissolved, setAgclDissolved] = useState(false);
  const [decanted, setDecanted] = useState(false);
  const [hno3Added, setHno3Added] = useState(false);
  const [agclReformed, setAgclReformed] = useState(false);
  const [clResultRecorded, setClResultRecorded] = useState(false);

  const getAgClColor = () => {
    if (!agno3Added) return 'transparent';
    if (nh4ohAdded && !hno3Added) return 'transparent'; // Dissolved
    return '#FAFAFA'; // White precipitate
  };

  const getAgIColor = () => {
    if (!agno3Added) return 'transparent';
    return '#FFEB3B'; // Yellow precipitate (doesn't dissolve)
  };

  const handlePrepareTubes = useCallback(() => {
    setTubesPrepared(true);
    if (onStepComplete) onStepComplete('halide-1');
  }, [onStepComplete]);

  const handleAddAgNO3 = useCallback(() => {
    if (tubesPrepared) {
      setIsDropping(true);
      setTimeout(() => {
        setIsDropping(false);
        setAgno3Added(true);
        if (onStepComplete) onStepComplete('halide-2');
      }, 500);
    }
  }, [tubesPrepared, onStepComplete]);

  const handleObservePrecipitates = useCallback(() => {
    if (agno3Added) {
      setPrecipitatesObserved(true);
      if (onStepComplete) onStepComplete('halide-3');
    }
  }, [agno3Added, onStepComplete]);

  const handleAddNH4OH = useCallback(() => {
    if (precipitatesObserved) {
      setIsDropping(true);
      setTimeout(() => {
        setIsDropping(false);
        setNh4ohAdded(true);
        setAgclDissolved(true);
        if (onStepComplete) onStepComplete('halide-4');
      }, 500);
    }
  }, [precipitatesObserved, onStepComplete]);

  const handleDecant = useCallback(() => {
    if (agclDissolved) {
      setDecanted(true);
      if (onStepComplete) onStepComplete('halide-5');
    }
  }, [agclDissolved, onStepComplete]);

  const handleAddHNO3 = useCallback(() => {
    if (decanted) {
      setIsDropping(true);
      setTimeout(() => {
        setIsDropping(false);
        setHno3Added(true);
        setAgclReformed(true);
        if (onStepComplete) onStepComplete('halide-6');
      }, 500);
    }
  }, [decanted, onStepComplete]);

  const handleRecordClResult = useCallback(() => {
    if (agclReformed) {
      setClResultRecorded(true);
      if (onStepComplete) onStepComplete('halide-7');
    }
  }, [agclReformed, onStepComplete]);

  return (
    <LabBench
      experimentName="Halide Detection (Cl- and I-)"
      completedCount={[tubesPrepared, agno3Added, precipitatesObserved, nh4ohAdded, decanted, agclReformed, clResultRecorded].filter(Boolean).length}
      totalSteps={7}
      equipment={['Cl- solution', 'I- solution', '0.1M AgNO₃', 'Concentrated NH₄OH', '6M HNO₃']}
      labMode="guided"
      currentStep={currentStep}
    >
      <div className="halide-test">
        {/* Equipment Area */}
        <div className="equipment-row">
          {/* Chloride Test Tube */}
          <div className="equipment-section">
            <svg viewBox="0 0 60 130" width={55} height={120}>
              <defs>
                <linearGradient id="tube-glass-cl" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                  <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                  <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                </linearGradient>
              </defs>

              {/* Test tube rim */}
              <ellipse cx="30" cy="12" rx="14" ry="5" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Test tube body */}
              <path d="M16 12 L16 100 Q16 115 30 115 Q44 115 44 100 L44 12" fill="url(#tube-glass-cl)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Liquid fill */}
              {tubesPrepared && (
                <path
                  d="M18 40 L18 98 Q18 112 30 112 Q42 112 42 98 L42 40 Z"
                  fill="#F5F5F5"
                  opacity="0.7"
                />
              )}

              {/* AgCl precipitate */}
              {agno3Added && !agclDissolved && (
                <g>
                  <ellipse cx="30" cy="95" rx="10" ry="5" fill={getAgClColor()} stroke="#E0E0E0" strokeWidth="0.5" />
                  <ellipse cx="25" cy="85" rx="4" ry="2" fill="#FFFAFA" opacity="0.9" />
                  <ellipse cx="35" cy="80" rx="3" ry="1.5" fill="#FFFAFA" opacity="0.9" />
                </g>
              )}

              {/* Reformed precipitate after HNO₃ */}
              {agclReformed && (
                <g>
                  <ellipse cx="30" cy="95" rx="10" ry="5" fill="#FAFAFA" stroke="#E0E0E0" strokeWidth="0.5" />
                  <ellipse cx="25" cy="88" rx="4" ry="2" fill="#FFFAFA" />
                </g>
              )}

              {/* Glass shine */}
              <line x1="20" y1="18" x2="20" y2="90" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span className="equipment-label">Cl- Solution</span>
            {agno3Added && (
              <span className="precipitate-label" style={{ color: '#9E9E9E' }}>
                {agclDissolved && !agclReformed ? 'Dissolved' : 'White ppt'}
              </span>
            )}
          </div>

          {/* Iodide Test Tube */}
          <div className="equipment-section">
            <svg viewBox="0 0 60 130" width={55} height={120}>
              <defs>
                <linearGradient id="tube-glass-i" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                  <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                  <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                </linearGradient>
              </defs>

              {/* Test tube rim */}
              <ellipse cx="30" cy="12" rx="14" ry="5" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Test tube body */}
              <path d="M16 12 L16 100 Q16 115 30 115 Q44 115 44 100 L44 12" fill="url(#tube-glass-i)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Liquid fill */}
              {tubesPrepared && (
                <path
                  d="M18 40 L18 98 Q18 112 30 112 Q42 112 42 98 L42 40 Z"
                  fill="#FFFDE7"
                  opacity="0.7"
                />
              )}

              {/* AgI precipitate - yellow, doesn't dissolve */}
              {agno3Added && (
                <g>
                  <ellipse cx="30" cy="95" rx="10" ry="5" fill={getAgIColor()} stroke="#FBC02D" strokeWidth="0.5" />
                  <ellipse cx="26" cy="87" rx="4" ry="2" fill="#FFEB3B" opacity="0.9" />
                  <ellipse cx="34" cy="82" rx="3" ry="1.5" fill="#FFEB3B" opacity="0.9" />
                </g>
              )}

              {/* Glass shine */}
              <line x1="20" y1="18" x2="20" y2="90" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span className="equipment-label">I- Solution</span>
            {agno3Added && (
              <span className="precipitate-label" style={{ color: '#FBC02D' }}>Yellow ppt</span>
            )}
          </div>

          {/* AgNO₃ Dropper */}
          <div className="equipment-section">
            <Dropper
              label="0.1M AgNO₃"
              liquidColor="#F5F5F5"
              onClick={tubesPrepared && !agno3Added ? handleAddAgNO3 : undefined}
              active={tubesPrepared && !agno3Added}
              dropping={isDropping && !agno3Added}
              disabled={!tubesPrepared || agno3Added}
            />
          </div>

          {/* NH₄OH Dropper */}
          {precipitatesObserved && !agclDissolved && (
            <div className="equipment-section">
              <Dropper
                label="Conc. NH₄OH"
                liquidColor="#F5F5F5"
                onClick={handleAddNH4OH}
                active={true}
                dropping={isDropping && precipitatesObserved}
              />
            </div>
          )}

          {/* HNO₃ Dropper */}
          {decanted && !hno3Added && (
            <div className="equipment-section">
              <Dropper
                label="6M HNO₃"
                liquidColor="#F5F5F5"
                onClick={handleAddHNO3}
                active={true}
                dropping={isDropping && decanted}
              />
            </div>
          )}
        </div>

        {/* Comparison Panel */}
        {agno3Added && (
          <div className="comparison-panel">
            <h4>Precipitate Comparison</h4>
            <div className="comparison-grid">
              <div className="comparison-item">
                <div className="color-swatch" style={{ backgroundColor: '#FAFAFA', border: '1px solid #E0E0E0' }} />
                <span>AgCl (White)</span>
              </div>
              <div className="comparison-item">
                <div className="color-swatch" style={{ backgroundColor: '#FFEB3B' }} />
                <span>AgI (Yellow)</span>
              </div>
            </div>
          </div>
        )}

        {/* Status Messages */}
        <div className="status-section">
          {!tubesPrepared && (
            <div className="instruction-card">
              <span className="step-badge">Step 1</span>
              <p>Prepare two test tubes: 5 mL <strong>Cl- solution</strong> and 5 mL <strong>I- solution</strong>.</p>
              <button className="action-btn" onClick={handlePrepareTubes}>
                <Droplets size={16} />
                Prepare Test Tubes
              </button>
            </div>
          )}

          {tubesPrepared && !agno3Added && (
            <div className="instruction-card">
              <span className="step-badge">Step 2</span>
              <p>Add <strong>5 drops of 0.1M AgNO₃</strong> to each tube.</p>
              <p className="hint">Click the AgNO₃ dropper.</p>
            </div>
          )}

          {agno3Added && !precipitatesObserved && (
            <div className="instruction-card">
              <span className="step-badge">Step 3</span>
              <p>Observe and <strong>compare the precipitate colors</strong>.</p>
              <button className="action-btn" onClick={handleObservePrecipitates}>
                <Check size={16} />
                Observe Precipitates
              </button>
            </div>
          )}

          {precipitatesObserved && !nh4ohAdded && (
            <div className="instruction-card">
              <span className="step-badge">Step 4</span>
              <p>Add <strong>concentrated NH₄OH</strong> to the AgCl precipitate.</p>
              <p className="hint">Click the NH₄OH dropper. Watch the white precipitate dissolve!</p>
            </div>
          )}

          {agclDissolved && !decanted && (
            <div className="instruction-card">
              <span className="step-badge">Step 5</span>
              <p><strong>Decant</strong> the clear supernatant to a new tube.</p>
              <button className="action-btn" onClick={handleDecant}>
                <Check size={16} />
                Decant Solution
              </button>
            </div>
          )}

          {decanted && !hno3Added && (
            <div className="instruction-card">
              <span className="step-badge">Step 6</span>
              <p>Add <strong>6M HNO₃</strong> drops and observe precipitate reformation.</p>
              <p className="hint">Click the HNO₃ dropper.</p>
            </div>
          )}

          {agclReformed && !clResultRecorded && (
            <div className="instruction-card success">
              <span className="step-badge">Step 7</span>
              <p>Record the <strong>positive result</strong> for chloride confirmation.</p>
              <button className="action-btn success" onClick={handleRecordClResult}>
                <Check size={16} />
                Record Result
              </button>
            </div>
          )}

          {clResultRecorded && (
            <div className="result-card">
              <Check size={20} />
              <div>
                <p><strong>Cl- Confirmed!</strong></p>
                <p>AgCl dissolved in NH3 and reprecipitated with acid, confirming chloride.</p>
                <p>AgI (yellow) does not dissolve in NH3, distinguishing it from AgCl.</p>
              </div>
            </div>
          )}
        </div>

        {/* Theory Box */}
        <div className="theory-box">
          <h4><Info size={14} /> Silver Halide Precipitates</h4>
          <p>
            Silver ions form insoluble precipitates with halide ions. The key difference:
            <strong> AgCl dissolves in NH3</strong> to form [Ag(NH3)2]+, while <strong>AgI does not</strong>.
            Acidifying the ammonia complex reprecipitates AgCl.
          </p>
          <div className="equation">
            Ag<sup>+</sup> + Cl<sup>-</sup> → AgCl(s) (white)
          </div>
          <div className="equation secondary">
            AgCl + 2NH<sub>3</sub> → [Ag(NH<sub>3</sub>)<sub>2</sub>]<sup>+</sup> + Cl<sup>-</sup>
          </div>
          <div className="equation secondary">
            [Ag(NH<sub>3</sub>)<sub>2</sub>]<sup>+</sup> + Cl<sup>-</sup> + 2H<sup>+</sup> → AgCl↓ + 2NH<sub>4</sub><sup>+</sup>
          </div>
        </div>

        <style>{`
          .halide-test {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
            max-width: 700px;
          }

          .equipment-row {
            display: flex;
            gap: 30px;
            align-items: flex-end;
            flex-wrap: wrap;
            justify-content: center;
          }

          .equipment-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .precipitate-label {
            font-size: 10px;
            font-weight: 600;
          }

          .comparison-panel {
            background: var(--bg-elevated);
            padding: 12px 20px;
            border-radius: 8px;
            width: 100%;
          }

          .comparison-panel h4 {
            margin: 0 0 10px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .comparison-grid {
            display: flex;
            gap: 20px;
            justify-content: center;
          }

          .comparison-item {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
          }

          .color-swatch {
            width: 20px;
            height: 20px;
            border-radius: 50%;
          }

          .status-section {
            width: 100%;
            min-height: 100px;
          }

          .instruction-card {
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
            border-left: 4px solid var(--primary);
          }

          .instruction-card.success {
            border-left-color: #00A86B;
          }

          .step-badge {
            display: inline-block;
            background: var(--primary);
            color: white;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
            margin-bottom: 8px;
          }

          .instruction-card p {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-primary);
          }

          .instruction-card .hint {
            font-size: 12px;
            color: var(--text-muted);
            font-style: italic;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 20px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            margin-top: 8px;
          }

          .action-btn:hover {
            background: var(--primary-dark);
            transform: translateY(-2px);
          }

          .action-btn.success {
            background: #00A86B;
          }

          .action-btn.success:hover {
            background: #008B5B;
          }

          .result-card {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 16px 20px;
            background: rgba(0, 168, 107, 0.1);
            border-radius: 12px;
            color: #00A86B;
          }

          .result-card p {
            margin: 0 0 4px 0;
            color: var(--text-primary);
          }

          .result-card p:first-child {
            color: #00A86B;
            font-weight: 600;
          }

          .theory-box {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
          }

          .theory-box h4 {
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .theory-box p {
            margin: 0 0 12px 0;
            font-size: 12px;
            color: var(--text-primary);
            line-height: 1.6;
          }

          .theory-box .equation {
            text-align: center;
            font-size: 13px;
            font-family: 'Times New Roman', serif;
            color: var(--text-secondary);
            background: var(--bg-dark);
            padding: 8px;
            border-radius: 6px;
            margin-bottom: 6px;
          }

          .theory-box .equation.secondary {
            font-size: 11px;
          }

          .info-buttons-row {
            display: flex;
            gap: 8px;
            justify-content: center;
            flex-wrap: wrap;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {/* Info Buttons */}
        <div className="info-buttons-row">
          {[1, 2, 3, 4, 5, 6, 7].map((stepId) => (
            stepInfoContent[stepId] && (
              <button
                key={stepId}
                className="step-info-btn"
                onClick={(e) => { e.stopPropagation(); setShowInfo(stepId); }}
                title={stepInfoContent[stepId].title}
              >
                ?
              </button>
            )
          ))}
        </div>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp3"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
    </LabBench>
  );
}
